import { fetchClient } from "@/api/api";
import type {
	acknowledgeBannerInput,
	acknowledgeBannerOutput,
	createBannerInput,
	createBannerOutput,
	deleteBannerInput,
	deleteBannerOutput,
	getActiveBannersInput,
	getActiveBannersOutput,
	getBannersInput,
	getBannersOutput,
	updateBannerInput,
	updateBannerOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { useTranslation } from "@/hooks/useTranslation";
import {
	type CustomMutationHook,
	type CustomQueryHook,
	queryClient,
} from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BANNER_SERVICE_KEYS } from "./keys";

const useGetActiveBanners: CustomQueryHook<
	getActiveBannersOutput,
	getActiveBannersInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: BANNER_SERVICE_KEYS.useGetActiveBanners(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/banner/", {
				params: { query: payload },
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

const useGetBanners: CustomQueryHook<getBannersOutput, getBannersInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: BANNER_SERVICE_KEYS.useGetBanners(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/admin/banners/", {
				params: { query: payload },
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

const invalidateBannerLists = () => {
	queryClient.invalidateQueries({
		queryKey: BANNER_SERVICE_KEYS.useGetActiveBanners(),
	});
	queryClient.invalidateQueries({
		queryKey: BANNER_SERVICE_KEYS.useGetBanners(),
	});
};

const useCreateBanner: CustomMutationHook<
	createBannerInput,
	createBannerOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useCreateBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST("/v1/fc/banner/", {
				body: payload,
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.banners.toasts.created"));
			invalidateBannerLists();
		},
	});
};

const useUpdateBanner: CustomMutationHook<
	updateBannerInput,
	updateBannerOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useUpdateBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/banner/{banner_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.banners.toasts.updated"));
			invalidateBannerLists();
		},
	});
};

const useDeleteBanner: CustomMutationHook<
	deleteBannerInput,
	deleteBannerOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useDeleteBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/fc/banner/{banner_id}",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.banners.toasts.deleted"));
			invalidateBannerLists();
		},
	});
};

const useAcknowledgeBanner: CustomMutationHook<
	acknowledgeBannerInput,
	acknowledgeBannerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useAcknowledgeBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/banner/{banner_id}/acknowledge",
				{
					params: {
						path: payload.path,
						query: payload.query,
					},
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: BANNER_SERVICE_KEYS.useGetActiveBanners(),
			});
		},
	});
};

export {
	useGetActiveBanners,
	useGetBanners,
	useCreateBanner,
	useUpdateBanner,
	useDeleteBanner,
	useAcknowledgeBanner,
};
